import { createTranslator } from 'use-intl';
import type { Locale } from '@financialharmony/i18n';
import englishMessages from '@financialharmony/i18n/en';
import type { Messages } from '@financialharmony/i18n/messages';
import { i18nHandlers, setAlertLocale } from './i18nAlerts';

// The catalog LocaleProvider currently shows. English until the provider has loaded one.
let active: { locale: Locale; messages: Messages } = { locale: 'en', messages: englishMessages };

/** Called by LocaleProvider whenever the catalog on screen changes, so non-React code speaks the same language. */
export function setActiveCatalog(locale: Locale, messages: Messages): void {
  active = { locale, messages };
  setAlertLocale(locale);
}

type Values = Record<string, string | number | Date>;

function translatorFor(namespace: string) {
  return createTranslator({
    locale: active.locale,
    messages: active.messages,
    namespace,
    timeZone: 'Europe/Stockholm',
    ...i18nHandlers,
  } as Parameters<typeof createTranslator>[0]) as unknown as ((path: string, values?: Values) => string) & {
    has(path: string): boolean;
  };
}

/** Whether `<namespace>.<path>` exists in the catalog on screen. */
export function hasTranslation(key: string): boolean {
  const dot = key.indexOf('.');
  return translatorFor(key.slice(0, dot)).has(key.slice(dot + 1));
}

/**
 * Text for code that has no React context (API clients, services, utilities). `key` is
 * `<namespace>.<path>`, e.g. `consultantToasts.api.fiscalYearCreated`. Inside a component use
 * `useTranslations` instead.
 */
export function translateOutsideReact(key: string, values?: Values): string {
  const dot = key.indexOf('.');
  return translatorFor(key.slice(0, dot))(key.slice(dot + 1), values);
}
